import React, { useState, useEffect } from "react";
import styles from "./ActiveCustomers.module.sass";
import cn from "classnames";
import Card from "../../../components/Card";
import Dropdown from "../../../components/Dropdown";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useDarkMode from "use-dark-mode";
import { getEvolutionData } from "../../../services/sessionDashboard.service";

const EvolutionProspect = ({ className, evolutionData }) => {
  const darkMode = useDarkMode(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Si les données sont passées en props, les utiliser
        if (evolutionData) {
          setData(evolutionData.evolution || []);
          setError(null);
        } else {
          // Sinon, récupérer les données via l'API
          const result = await getEvolutionData();
          setData(result.evolution || []);
          setError(null);
        }
      } catch (err) {
        console.error("Failed to fetch evolution data:", err);
        setError("Impossible de charger les données d'évolution des sessions.");
        // Données par défaut en cas d'erreur avec le même format que les prospects
        const defaultData = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const formattedDate = date.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
          
          defaultData.push({
            name: formattedDate,
            sessionsCreated: 0,
            sessionsStarted: 0
          });
        }
        setData(defaultData);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [evolutionData]);

  return (
    <Card
      className={cn(styles.card, className)}
      title="Sessions"
      classTitle={cn("title-green", styles.cardTitle)}
      classCardHead={styles.cardHead}
    >
      {loading && <p>Chargement des données d'évolution...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && !error && data.length > 0 && (
        <div className={styles.chart}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={500}
              height={300}
              data={data}
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
            >
              <CartesianGrid
                strokeDasharray="none"
                stroke={darkMode.value ? "#272B30" : "#EFEFEF"}
                vertical={false}
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fontWeight: "500", fill: "#9A9FA5" }}
                padding={{ left: 10 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fontWeight: "500", fill: "#9A9FA5" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#272B30",
                  borderColor: "rgba(255, 255, 255, 0.12)",
                  borderRadius: 8,
                  boxShadow:
                    "0px 4px 8px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1), inset 0px 0px 1px #000000",
                }}
                labelStyle={{ fontSize: 12, fontWeight: "500", color: "#fff" }}
                itemStyle={{
                  padding: 0,
                  textTransform: "capitalize",
                  fontSize: 12,
                  fontWeight: "600",
                  color: "#fff",
                }}
                formatter={(value, name) => {
                  const labels = {
                    sessionsCreated: 'Sessions créées',
                    sessionsStarted: 'Sessions démarrées'
                  };
                  return [`${value}`, labels[name] || name];
                }}
              />
              <Line
                type="monotone"
                dataKey="sessionsCreated"
                dot={false}
                strokeWidth={4}
                stroke="#B5E4CA"
                name="sessionsCreated"
              />
             
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {!loading && !error && data.length === 0 && (
        <p>Aucune donnée d'évolution disponible.</p>
      )}
    </Card>
  );
};

export default EvolutionProspect;
